import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import Modal, { ModalProps } from '@/components/utilities/modal/modal';
import useClickOutside from '@/libraries/hooks/use-click-outside';
import styles from './slide-in.module.scss';

export interface SlideInProps extends Omit<ModalProps, 'children'> {
  /** The content to render as a React node */
  children?: React.ReactNode;
  /** The class name to apply */
  className?: string;
  /** Target id where to scroll on open */
  scrollTargetId?: string;
}

const SlideIn = ({
  children,
  className,
  isOpen,
  scrollTargetId,
  setIsOpen,
  ...rest
}: SlideInProps) => {
  const slideInRef = useRef<HTMLDivElement>(null);

  useClickOutside(slideInRef, () => setIsOpen?.(false), isOpen);

  useEffect(() => {
    if (!isOpen) return;

    if (!scrollTargetId) return;

    const targetElement = document.getElementById(scrollTargetId);

    if (!targetElement) return;

    targetElement.scrollIntoView({ behavior: 'smooth' });
  }, [isOpen, scrollTargetId]);

  return (
    <Modal
      {...rest}
      className={classNames(styles.component, className)}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <div ref={slideInRef} className={styles.curtain}>
        {children}
      </div>
    </Modal>
  );
};

SlideIn.displayName = 'SlideIn';

export default SlideIn;
